import { CheckCircleIcon } from "@chakra-ui/icons";
import { Box, BoxProps, Flex, FlexProps, Text, useEditableControls } from "@chakra-ui/react";
import { ReactNode, createContext, useContext } from "react";
import { MdOutlineEdit } from "react-icons/md";
import { FormattedMessage, useIntl } from "react-intl";

import { ContextMenu } from "@nens/chakra/components";
import { MessagesInner } from "@nens/rana/api-v1";
import { RanaEditable } from "@nens/ranalib";
import { BORDER_WIDTHS, RanaContextMenu, SIZES, TrashIcon } from "@nens/ranalib";

import { useProjectActionAllowed } from "@/hooks/use-action-allowed-in-project";

export const COMMENT_MESSAGE_P_PX = SIZES[2];
export const COMMENT_MESSAGE_BORDER_WIDTH_PX = BORDER_WIDTHS.base;

type MessageAction = {
  label: string;
  action: React.MouseEventHandler | (() => void);
  icon: ReactNode;
};

const ActionsContext = createContext<MessageAction[]>([]);

interface MessageWrapperProps extends FlexProps {
  children: ReactNode;
  message: MessagesInner;
  editMessage: (body: string) => void;
  removeMessage: () => void;
  resolved?: boolean;
  resolveMessage?: () => void;
}

export const MessageWrapper = ({
  children,
  message,
  editMessage,
  removeMessage,
  resolved,
  resolveMessage,
  ...rest
}: MessageWrapperProps) => {
  const { body } = message;
  return (
    <RanaEditable
      // Use key to make sure it resets when body changes
      key={body}
      defaultValue={body}
      isPreviewFocusable={false}
      submitOnBlur={false}
      selectAllOnFocus
      onSubmit={editMessage}
    >
      <MessageActionsWrapper
        message={message}
        removeMessage={removeMessage}
        resolved={resolved}
        resolveMessage={resolveMessage}
        {...rest}
      >
        {children}
      </MessageActionsWrapper>
    </RanaEditable>
  );
};

const MessageCreatorName = ({ creator }: { creator: MessagesInner["creator"] }) => {
  const name = `${creator.givenName} ${creator.familyName}`;
  return (
    <Text as="strong">
      {name} <br />
    </Text>
  );
};
MessageWrapper.CreatorName = MessageCreatorName;

const MessageText = () => {
  return (
    <>
      <RanaEditable.Preview />
      <RanaEditable.Input display="inline" />
    </>
  );
};
MessageWrapper.Text = MessageText;

interface MessageActionsProps extends FlexProps {
  children: ReactNode;
  message: MessagesInner;
  removeMessage: () => void;
  resolved?: boolean;
  resolveMessage?: () => void;
}

// Must be placed inside <MessageWrapper> (i.e. inside <Editable>) so that
// useEditableControls works. Acts as both the ContextMenu provider for
// right-click and an ActionsContext provider for the hover button child.
const MessageActionsWrapper = ({
  children,
  message,
  resolved,
  removeMessage,
  resolveMessage,
  ...rest
}: MessageActionsProps) => {
  const intl = useIntl();
  const { canWriteProject } = useProjectActionAllowed();

  const { getEditButtonProps } = useEditableControls();

  const actions: MessageAction[] = [];

  if (canWriteProject) {
    actions.push(
      {
        label: intl.formatMessage({ id: "button.delete", defaultMessage: "Delete" }),
        action: removeMessage,
        icon: <TrashIcon />,
      },
      {
        label: intl.formatMessage({ id: "button.edit", defaultMessage: "Edit" }),
        action: getEditButtonProps().onClick ?? (() => undefined),
        icon: <MdOutlineEdit />,
      }
    );

    if (resolveMessage)
      actions.push({
        label: resolved
          ? intl.formatMessage({ id: "button.unresolve", defaultMessage: "Unresolve" })
          : intl.formatMessage({ id: "button.resolve", defaultMessage: "Resolve" }),
        action: resolveMessage,
        icon: <CheckCircleIcon />,
      });
  }

  return (
    <ActionsContext.Provider value={actions}>
      <ContextMenu<HTMLDivElement>
        renderMenu={actions.length ? () => <RanaContextMenu.Actions actions={actions} /> : null}
      >
        {(ref) => (
          <Flex ref={ref} role="group" w="full" {...rest}>
            {children}
          </Flex>
        )}
      </ContextMenu>
    </ActionsContext.Provider>
  );
};

// Hover button — reads actions from the nearest MessageWrapper provider.
const MessageActions = ({ ...props }: BoxProps) => {
  const actions = useContext(ActionsContext);
  if (!actions.length) return null;
  return (
    <Box visibility="hidden" _groupHover={{ visibility: "visible" }} {...props}>
      <RanaContextMenu actions={actions} />
    </Box>
  );
};
MessageWrapper.Actions = MessageActions;

const MessageTime = ({
  message,
  getPrettyTimeFunc,
}: {
  message: MessagesInner;
  getPrettyTimeFunc: (date: Date) => string;
}) => {
  const { createdAt, edited, updatedAt } = message;

  if (edited)
    return (
      <>
        <FormattedMessage id="comment.edited" defaultMessage="Edited" />{" "}
        {updatedAt && getPrettyTimeFunc(updatedAt)}
      </>
    );

  return getPrettyTimeFunc(createdAt);
};
MessageWrapper.MessageTime = MessageTime;
